/**
 * EBO-ALERTS sync: drains queued VHF signal reports into
 * public.vhf_signal_reports and requests a rule-based prioritisation.
 * Human-in-the-loop only — the engine never auto-notifies.
 */
import { supabase } from '@/integrations/supabase/client';
import { safeIsOnline } from '@/lib/browserSafeUtils';
import { logger } from '@/lib/logger';
import { vhfSignalOfflineDb } from './vhfSignalOfflineDb';

let inflight = false;

export async function syncVhfSignals(): Promise<{ synced: number; failed: number }> {
  if (inflight || !safeIsOnline()) return { synced: 0, failed: 0 };
  inflight = true;
  let synced = 0;
  let failed = 0;

  try {
    const queued = await vhfSignalOfflineDb.getUnsynced();
    for (const q of queued) {
      try {
        const { data, error } = await (supabase as any)
          .from('vhf_signal_reports')
          .insert({
            signal_group: q.signalGroup,
            source: q.source,
            source_channel: q.sourceChannel,
            facility_id: q.facilityId,
            organization_id: q.organizationId,
            village: q.village,
            health_zone: q.healthZone,
            district_code: q.districtCode,
            country_code: q.countryCode,
            gps_lat: q.gpsLat,
            gps_lng: q.gpsLng,
            onset_at: q.onsetAt,
            reported_at: q.reportedAt,
            denominator: q.denominator,
            notes: q.notes,
            reported_by: q.reportedBy,
            local_sync_id: q.localId,
          })
          .select('id')
          .single();
        if (error) throw error;
        await vhfSignalOfflineDb.markSynced(q.localId, data.id);

        // Fire-and-forget prioritisation (silent mode by default).
        void supabase.functions
          .invoke('vhf-prioritize-signal', { body: { signal_id: data.id } })
          .catch((err) => logger.warn('[EBO-ALERTS] prioritise failed', err));

        synced++;
      } catch (err) {
        logger.error('[EBO-ALERTS] Sync failed for signal', q.localId, err);
        failed++;
      }
    }
  } finally {
    inflight = false;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('vhf-signal-sync-complete', { detail: { synced, failed } }));
  }
  return { synced, failed };
}
